import { Close } from '@mui/icons-material';
import { IconButton, Snackbar, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { EInteractionMode, EWallMode } from '../../@types/Enums';
import { useSingleSelectContext } from '../../provider/SingleSelectProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { immersightBlue } from '../../utils/colors';

/**
 * This is a utility component that listens for changes in the currently active mode of interaction,
 * and informs the user about his options.
 *
 * @returns Snackbar that displays information to the user.
 */
const ModeToast = () => {
  const { wallMode, setWallMode, isDrawingAllowed, setIsDrawingAllowed, interactionMode, setInteractionMode } = useToolbarContext();
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [exitMessage, setExitMessage] = useState<string>('');
  const { setCurrentlySelected, items } = useSingleSelectContext();
  const intl = useIntl();


  useEffect(() => {
    if (interactionMode === EInteractionMode.ATTACHMENT) {
      setMessage(intl.formatMessage({ id: 'ModeToast.attachmentMode' }));
      setExitMessage(intl.formatMessage({ id: 'ModeToast.attachmentModeExit' }));
      setOpen(true);
    } else if (interactionMode === EInteractionMode.MEASURE) {
      setMessage(intl.formatMessage({ id: 'ModeToast.measureMode' }));
      setExitMessage(intl.formatMessage({ id: 'ModeToast.measureModeExit' }));
      setOpen(true);
    } else if (interactionMode === EInteractionMode.SET_TEXTURE) {
      setMessage('Klicken Sie nun auf ein Boden, ein Element oder ein Model, um die Oberfläche zu setzen');
      setExitMessage('Abbrechen');
      setOpen(true);
    } else if (wallMode === EWallMode.NONE && !isDrawingAllowed) {
      // No active mode, close the toast
      setOpen(false);
    } else {
      // Handle Wall Modes
      let modeMessage = '';
      let modeExitMessage = '';

      if (interactionMode === EInteractionMode.DRAW) {
        modeMessage = intl.formatMessage({ id: 'ModeToast.drawingMode' });
        modeExitMessage = intl.formatMessage({
          id: 'ModeToast.drawingModeExit',
        });
      } else {
        switch (wallMode) {
          case EWallMode.DOOR:
            modeMessage = intl.formatMessage({ id: 'ModeToast.door' });
            modeExitMessage = intl.formatMessage({ id: 'ModeToast.doorExit' });
            break;
          case EWallMode.PASSAGE:
            modeMessage = intl.formatMessage({ id: 'ModeToast.passage' });
            modeExitMessage = intl.formatMessage({ id: 'ModeToast.passageExit' });
            break;
          case EWallMode.WINDOW:
            modeMessage = intl.formatMessage({ id: 'ModeToast.window' });
            modeExitMessage = intl.formatMessage({
              id: 'ModeToast.windowExit',
            });
            break;
          case EWallMode.PARTITION:
            modeMessage = intl.formatMessage({ id: 'ModeToast.partitionWall' });
            modeExitMessage = intl.formatMessage({
              id: 'ModeToast.partitionWallExit',
            });
            break;
          case EWallMode.RADIATOR:
            modeMessage = intl.formatMessage({ id: 'ModeToast.radiator' });
            modeExitMessage = intl.formatMessage({
              id: 'ModeToast.radiatorExit',
            });
            break;
          default:
            return; // Don't open the snackbar if no valid mode
        }
      }
      if (modeMessage) {
        setMessage(modeMessage);
        setExitMessage(modeExitMessage);
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
  }, [wallMode, intl, isDrawingAllowed, interactionMode]);

  /**
   * Function to close the currently active snackbar and exit the associated mode as well.
   * Will get called when the user manually closes (and thereby exits) from the current mode.
   */
  const exitModeAndCloseSnackbar = (): void => {
    setIsDrawingAllowed(false);
    setWallMode(EWallMode.NONE);
    setInteractionMode(EInteractionMode.NONE);
    // TODO lets use our new enums for setting it back to select mode
    setCurrentlySelected(items.find((item) => item.id === 0) || items[0]);
  };

  /**
   * Creates the exit action that will get added to the snackbar.
   * This action will close the displayed snackbar and exit from the currently active mode.
   *
   * @returns {JSX.Element} IconButton containing a close icon, wrapped in a Tooltip to display the exit message.
   */
  const exitAction = (): JSX.Element => (
    <Tooltip title={exitMessage} placement={'top-start'}>
      <IconButton size="small" onClick={() => exitModeAndCloseSnackbar()}>
        <Close
          sx={{
            color: 'rgb(222, 222, 222)',
            transition: 'ease-in-out 150ms color',
            '&:hover': {
              color: 'white',
              transition: 'ease-in-out 150ms color',
            },
          }}
        />
      </IconButton>
    </Tooltip>
  );

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        '.MuiPaper-root': {
          backgroundColor: immersightBlue,
          display: 'flex',
          position: 'relative',
        },
        '& .MuiSnackbarContent-action': {
          display: 'flex',
          position: 'absolute',
          right: 0,
          top: 0,
          transform: 'translate(-.9rem, .4rem)',
        },
        '& .MuiSnackbarContent-message': {
          display: 'flex',
          marginRight: '2rem',
        },
      }}
      message={message}
      action={exitAction()}
    />
  );
};

export default ModeToast;
